import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useEffect, useRef, type ReactNode } from 'react';
import { useDrop } from 'react-dnd';
import { useIntl } from 'react-intl';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import { styled, alpha } from '@mui/material/styles';
import cx from 'classnames';

import {
  brainCategorizationEnabledSelector,
  subtypeFilterSelector,
  setSubtypeFilterAction,
  documentsChangeTypesAction,
} from 'domain/documents';
import { useWorkspaceSubtypes } from 'pages/company/WorkspaceRoute/hooks/useWorkspaceAggregations';
import { rootCategoriesListSelector } from 'domain/categories';
import { isInboxCategory, findCatById } from 'domain/categories/helpers';
import { DOCUMENT } from 'components/DnDItem';
import toast from 'components/Toast';

// Inbox-only sub-type chip row (spec da-19295 req6). Renders the present sub-type facet from the
// search_new aggregations block (backend-driven labels, incl. the All default and Unclassified).
// A chip click sets the server sub-type filter (task 3.3). A specific sub-type chip is also a
// reclassify drop target (task 4.1): dropping a document — or the current selection — assigns that
// sub-type. The All and Unclassified chips are not drop targets (base-financial goes via the tab).

const ChipRow = styled(Stack, { label: 'WorkspaceSubtypeChips' })(({ theme }) => ({
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  padding: theme.spacing(2, 0, 1, 0),
}));

const DropSpan = styled('span', { label: 'WorkspaceSubtypeChipDrop' })(({ theme }) => ({
  display: 'inline-flex',
  borderRadius: 16,
  '&.is-drag-over': {
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    outline: `1px dashed ${theme.palette.primary.main}`,
  },
}));

// A specific sub-type chip is a reclassify drop target (spec 4.1): drop writes that sub-type id.
// The All chip (id:null) and the Unclassified facet are NOT targets — base-financial reclassify
// goes through the Financial tab. Always a bulk write over the current selection.
const DroppableChip = ({
  writeType,
  subtypeLabel,
  selectedIds,
  children,
}: {
  writeType: string;
  subtypeLabel: string;
  selectedIds: string[];
  children: ReactNode;
}) => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const ref = useRef<HTMLSpanElement>(null);
  // The drag item comes from the legacy DnDItemSource (components/DnDItem); its runtime shape is
  // `{ id, ...params, itemType }` and we read `id`. `type` is not on the item — it is here only
  // because react-dnd v7's useDrop generic must extend DragObjectWithType ({ type }).
  const [{ isOver, canDrop }, drop] = useDrop<{ type: string; id: string }, unknown, { isOver: boolean; canDrop: boolean }>({
    accept: DOCUMENT,
    drop: (item) => {
      if (!item?.id) return;
      const documentsIds = selectedIds.length ? selectedIds : [item.id];
      new Promise((resolve, reject) => {
        dispatch({ type: documentsChangeTypesAction.type, payload: { documentsIds, type: writeType, resolve, reject } });
      })
        .then(() =>
          toast.info(
            formatMessage(
              { id: 'document.actions.subtype.success', defaultMessage: 'Document marked as {subtype}' },
              { subtype: subtypeLabel },
            ),
          ),
        )
        .catch(() => null);
    },
    collect: (monitor) => ({ isOver: monitor.isOver(), canDrop: monitor.canDrop() }),
  });
  drop(ref);
  return (
    <DropSpan ref={ref} className={cx({ 'is-drag-over': isOver && canDrop })}>
      {children}
    </DropSpan>
  );
};

const WorkspaceSubtypeChips = ({ selectedIds = [] }: { selectedIds?: string[] }) => {
  const dispatch = useDispatch();
  const enabled = useSelector(brainCategorizationEnabledSelector);
  const subtypes = useWorkspaceSubtypes();
  const selected = useSelector(subtypeFilterSelector);
  const rootCategoriesList = useSelector(rootCategoriesListSelector);
  const { category1 } = useParams();

  const isInbox = isInboxCategory(findCatById(rootCategoriesList, category1));

  // DA-19681: the sub-type filter persists across Inbox navigation (§7.3). If the destination Inbox
  // category doesn't offer the selected sub-type (it's not among the present sub-tags), reset to All —
  // otherwise it stays applied-but-unhighlighted and would re-activate in a later category that has
  // it. Guarded on subtypes.length so it fires only once the facet for this category has loaded.
  useEffect(() => {
    if (isInbox && selected && subtypes.length > 0 && !subtypes.some((s) => s.id === selected)) {
      dispatch(setSubtypeFilterAction(null));
    }
  }, [isInbox, selected, subtypes, dispatch]);

  if (!enabled || !isInbox || subtypes.length === 0) return null;

  // Toggle: re-click the active chip clears the filter. The backend "All" chip (id:null) always
  // clears it. The saga refetches on this action.
  const onChipClick = (id: string | null) => dispatch(setSubtypeFilterAction(selected === id ? null : id));

  return (
    <ChipRow>
      {subtypes.map(({ id, label }) => {
        const chip = (
          <Chip
            label={label}
            size="small"
            variant={selected === id ? 'filled' : 'outlined'}
            color={selected === id ? 'primary' : 'default'}
            onClick={() => onChipClick(id)}
          />
        );
        // All chip (id:null) and the Unclassified facet are not drop targets (base-financial
        // reclassify goes via the Financial tab); only specific sub-types accept a drop.
        if (id === null || id === '_S_IS_UNCLASSIFIED') return <span key={id ?? 'all'}>{chip}</span>;
        return (
          <DroppableChip key={id} writeType={id} subtypeLabel={label} selectedIds={selectedIds}>
            {chip}
          </DroppableChip>
        );
      })}
    </ChipRow>
  );
};

export default WorkspaceSubtypeChips;
